/**
 * "Generate my key", without leaving the page: issues an agent key for the signed-in person's own
 * developer, shows it once, and gives the one command that stores it on their computer — with the
 * key already in it, for the system they are on.
 */

import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { KeyRound, ShieldAlert } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { CodeBlock, Segmented } from './primitives'
import { Modal } from './Modal'
import { VaultButton } from './VaultButton'
import { guessSystem, storeKeyCommand, type KeySystem } from '../lib/keySetup'

export function KeyModal({ onClose, onIssued }: { onClose: () => void; onIssued?: () => void }) {
  const { t } = useI18n()
  const [machine, setMachine] = useState('')
  const [system, setSystem] = useState<KeySystem>(() => guessSystem())

  const issue = useMutation({
    mutationFn: () => api.me.issueCredential({ name: machine.trim() || undefined, machineLabel: machine.trim() || undefined }),
    onSuccess: () => onIssued?.(),
  })

  const key = issue.data?.key
  const notLinked = issue.error instanceof ApiError && (issue.error.code === 'not_a_developer' || issue.error.status === 403)

  return (
    <Modal title={t('connect.key.title')} onClose={onClose} wide>
      {!key ? (
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault()
            if (!issue.isPending) issue.mutate()
          }}
        >
          <p className="small secondary">{t('connect.key.what')}</p>
          <label className="field">
            <span className="field__label">{t('connect.key.machine')}</span>
            <input
              className="input"
              value={machine}
              onChange={(event) => setMachine(event.target.value)}
              placeholder={t('connect.key.machinePlaceholder')}
              maxLength={100}
            />
            <span className="field__hint">{t('connect.key.machineHint')}</span>
          </label>
          {notLinked && (
            <div className="connect__warning small" role="alert">
              <ShieldAlert size={14} aria-hidden /> {t('connect.key.notLinked')}
            </div>
          )}
          {issue.error && !notLinked && <span className="field__error">{t('error.generic')}</span>}
          <div className="row row--tight" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn--sm btn--ghost" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn--sm btn--primary" disabled={issue.isPending}>
              <KeyRound size={13} aria-hidden /> {t('connect.key.generate')}
            </button>
          </div>
        </form>
      ) : (
        <div className="stack">
          <div className="connect__warning small" role="note">
            <ShieldAlert size={14} aria-hidden /> {t('connect.key.once')}
          </div>

          <div className="stack stack--tight">
            <span className="field__label">{t('connect.key.yourKey')}</span>
            <CodeBlock code={key} copyLabel={t('common.copy')} copiedLabel={t('common.copied')} />
          </div>

          {/* In the desktop app the key goes straight into this machine's credential store. */}
          <VaultButton value={key} />

          <div className="stack stack--tight">
            <span className="field__label">{t('connect.key.store')}</span>
            <Segmented
              value={system}
              options={[
                { value: 'windows', label: 'Windows' },
                { value: 'mac', label: 'macOS' },
                { value: 'linux', label: 'Linux' },
              ]}
              onChange={setSystem}
            />
            <span className="tiny muted">{t(`connect.key.where.${system}`)}</span>
            <CodeBlock code={storeKeyCommand(system, key)} copyLabel={t('common.copy')} copiedLabel={t('common.copied')} />
            <span className="small">{t('connect.key.restart')}</span>
          </div>

          <div className="row row--tight" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn--sm btn--primary" onClick={onClose}>
              {t('connect.key.done')}
            </button>
          </div>
        </div>
      )}
    </Modal>
  )
}
